import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Snackbar, Tab, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { apiHost } from '../../utils/apiHost';
import { barisPerubahan, batasHapusHari, batasHapusTahunan, buatCsv, hariDalamBulan, infoRetensi, labelKategori, labelPeran, NAMA_BULAN, penandaHapus, tanggalPendek, waktuLog } from '../../utils/logAktivitas';

// Log Aktivitas: apa yang dilakukan kasir, karyawan, dapur, dan admin. Admin cabang melihat cabangnya (tanpa aksi superadmin dan tanpa IP);
// superadmin melihat semua cabang dengan IP dan boleh menghapus manual. Server: controller/logAktivitasController.js.

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const tgl = (d) => { const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const pesanGalat = (e) => e?.response?.data?.message || e?.message || 'Gagal';

const warnaPeran = { superadmin: 'secondary', admin: 'primary', kasir: 'success', karyawan: 'info', dapur: 'warning' };

const RincianPerubahan = ({ r }) => {
  const baris = barisPerubahan(r.sebelum, r.sesudah);
  if (!baris.length) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      {baris.map((b) => (
        <Typography key={b.kolom} variant="caption" display="block" color="text.secondary">
          <b>{b.kolom}:</b> {b.lama !== null && b.baru !== null ? `${b.lama} → ${b.baru}` : b.lama !== null ? `${b.lama} (dihapus)` : b.baru}
        </Typography>
      ))}
    </Box>
  );
};

const BarisLog = ({ r, superadmin }) => {
  const [buka, setBuka] = useState(false);
  const adaPerubahan = barisPerubahan(r.sebelum, r.sesudah).length > 0;
  const hapus = penandaHapus(r.dihapusDalamHari);
  return (
    <>
      <TableRow hover sx={{ '& > td': { verticalAlign: 'top' } }}>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{waktuLog(r.waktu)}</TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{r.pengguna?.nama || r.pengguna?.username || '-'}</Typography>
          <Chip size="small" label={labelPeran(r.pengguna?.role)} color={warnaPeran[r.pengguna?.role] || 'default'} variant="outlined" sx={{ mt: 0.3 }} />
        </TableCell>
        {superadmin && <TableCell>{r.cabang || '-'}</TableCell>}
        <TableCell sx={{ minWidth: 260 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {r.tingkat === 'penting' && <Chip size="small" color="error" label="Penting" />}
            <Chip size="small" variant="outlined" label={labelKategori(r.kategori)} />
            {r.offline && <Chip size="small" variant="outlined" label="dari mode offline" />}
            {hapus && <Chip size="small" color="warning" variant="outlined" label={hapus} />}
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{r.ringkasan}</Typography>
          {adaPerubahan && (
            <Collapse in={buka} unmountOnExit><RincianPerubahan r={r} /></Collapse>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2">{r.lokasi || '-'}</Typography>
          {superadmin && r.ip && <Typography variant="caption" color="text.secondary" display="block">IP {r.ip}</Typography>}
          {r.perangkat && <Typography variant="caption" color="text.secondary" display="block">{r.perangkat}</Typography>}
        </TableCell>
        <TableCell padding="checkbox">
          {adaPerubahan && (
            <Tooltip title={buka ? 'Sembunyikan nilai lama → baru' : 'Lihat nilai lama → baru'}>
              <IconButton size="small" onClick={() => setBuka(!buka)} aria-label="Lihat perubahan">{buka ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};

const DialogHapus = ({ open, onClose, cabang, onSelesai }) => {
  const [mode, setMode] = useState('sebelum');
  const [sebelum, setSebelum] = useState('');
  const [cab, setCab] = useState('');
  const [konfirmasi, setKonfirmasi] = useState('');
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const kirim = async () => {
    setProses(true); setGalat('');
    try {
      const params = { konfirmasi };
      if (mode === 'sebelum') params.sebelum = sebelum;
      if (mode === 'cabang') params.cabang = cab;
      if (mode === 'semua') params.semua = true;
      const r = await axios.delete(`${base()}/log-aktivitas`, { params, withCredentials: true });
      onSelesai(r.data?.message || 'Log dihapus');
      setKonfirmasi('');
      onClose();
    } catch (e) {
      setGalat(pesanGalat(e));
    } finally {
      setProses(false);
    }
  };
  const lengkap = (mode === 'sebelum' ? !!sebelum : mode === 'cabang' ? !!cab : true) && konfirmasi === 'HAPUS';
  return (
    <Dialog open={open} onClose={() => !proses && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Hapus log aktivitas</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Alert severity="warning">Log yang dihapus tidak bisa dikembalikan. Penghapusan ini sendiri akan tercatat. Ekspor dulu bila perlu arsip.</Alert>
        <TextField select label="Yang dihapus" value={mode} onChange={(e) => setMode(e.target.value)} size="small">
          <MenuItem value="sebelum">Log sebelum tanggal tertentu</MenuItem>
          <MenuItem value="cabang">Semua log satu cabang</MenuItem>
          <MenuItem value="semua">Semua log</MenuItem>
        </TextField>
        {mode === 'sebelum' && <TextField type="date" label="Sebelum tanggal" value={sebelum} onChange={(e) => setSebelum(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />}
        {mode === 'cabang' && (
          <TextField select label="Cabang" value={cab} onChange={(e) => setCab(e.target.value)} size="small">
            {(cabang || []).map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </TextField>
        )}
        <TextField label="Ketik HAPUS untuk mengonfirmasi" value={konfirmasi} onChange={(e) => setKonfirmasi(e.target.value)} size="small" />
        {galat && <Alert severity="error">{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button color="error" variant="contained" onClick={kirim} disabled={!lengkap || proses}>{proses ? 'Menghapus…' : 'Hapus'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const PILIHAN_MASA = [[30, '1 bulan (30 hari)'], [90, '3 bulan (90 hari)'], [180, '6 bulan (180 hari)'], [365, '1 tahun (365 hari)'], [730, '2 tahun (730 hari)'], [1095, '3 tahun (1095 hari)']];

// Masa simpan log: pilihan hari bergulir, atau hapus tahunan (setiap tanggal-bulan, log sebelum tanggal itu tahun lalu dihapus).
const DialogRetensi = ({ open, onClose, retensi, onSelesai }) => {
  const sekarang = retensi?.hariSimpan || 365;
  const min = retensi?.batas?.min || 30;
  const maks = retensi?.batas?.maks || 1095;
  const tahunanKini = retensi?.mode === 'tahunan' ? retensi.tahunan : null;
  const daftarHari = PILIHAN_MASA.filter(([h]) => h >= min && h <= maks);
  if (!tahunanKini && !daftarHari.some(([h]) => h === sekarang)) daftarHari.push([sekarang, `${sekarang} hari (saat ini)`]);
  const awal = () => (tahunanKini ? 'tahunan' : String(sekarang));
  const hariIni = new Date();
  const [pilih, setPilih] = useState(awal);
  const [bulan, setBulan] = useState(tahunanKini?.bulan || hariIni.getMonth() + 1);
  const [tanggal, setTanggal] = useState(tahunanKini?.tanggal || hariIni.getDate());
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  React.useEffect(() => {
    if (open) { setPilih(awal()); setBulan(tahunanKini?.bulan || new Date().getMonth() + 1); setTanggal(tahunanKini?.tanggal || new Date().getDate()); setGalat(''); }
  }, [open, sekarang, retensi?.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const tahunan = pilih === 'tahunan';
  const maksTanggal = hariDalamBulan(bulan);
  const tglSah = Math.min(tanggal, maksTanggal);
  const batasBaru = tahunan ? batasHapusTahunan(bulan, tglSah) : batasHapusHari(Number(pilih));
  const batasKini = retensi?.batasHapus ? new Date(retensi.batasHapus) : null;
  const tidakBerubah = tahunan ? (tahunanKini && tahunanKini.bulan === bulan && tahunanKini.tanggal === tglSah) : (!tahunanKini && Number(pilih) === sekarang);
  const kirim = async () => {
    setProses(true); setGalat('');
    try {
      const isi = tahunan ? { tahunan: { bulan, tanggal: tglSah } } : { hari: Number(pilih) };
      const r = await axios.put(`${base()}/log-aktivitas/retensi`, isi, { withCredentials: true });
      onSelesai(r.data?.message || 'Masa simpan diubah');
      onClose();
    } catch (e) {
      setGalat(pesanGalat(e));
    } finally {
      setProses(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !proses && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Atur masa simpan log</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">Berlaku untuk semua cabang. Log yang lebih lama dari masa simpan dihapus otomatis.</Typography>
        <TextField select label="Masa simpan" value={pilih} onChange={(e) => setPilih(e.target.value)} size="small">
          {daftarHari.map(([h, teks]) => <MenuItem key={h} value={String(h)}>{teks}</MenuItem>)}
          <MenuItem value="tahunan">Setiap tahun pada tanggal…</MenuItem>
        </TextField>
        {tahunan && (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField select label="Tanggal" value={tglSah} onChange={(e) => setTanggal(Number(e.target.value))} size="small" sx={{ flex: 1 }}>
                {Array.from({ length: maksTanggal }, (_, i) => i + 1).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField select label="Bulan" value={bulan} onChange={(e) => setBulan(Number(e.target.value))} size="small" sx={{ flex: 2 }}>
                {NAMA_BULAN.map((b, i) => <MenuItem key={b} value={i + 1}>{b}</MenuItem>)}
              </TextField>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Setiap {tglSah} {NAMA_BULAN[bulan - 1]}, log sebelum {tglSah} {NAMA_BULAN[bulan - 1]} tahun lalu dihapus. Log selalu tersimpan minimal 1 tahun penuh (1–2 tahun).
            </Typography>
          </>
        )}
        {!tidakBerubah && batasKini && batasBaru > batasKini && (
          <Alert severity="warning">Log sebelum {tanggalPendek(batasBaru)} akan langsung dihapus dan tidak bisa dikembalikan. Ekspor dulu bila perlu arsip.</Alert>
        )}
        {galat && <Alert severity="error">{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button variant="contained" onClick={kirim} disabled={proses || tidakBerubah}>{proses ? 'Menyimpan…' : 'Simpan'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const LogAktivitas = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const [kelompok, setKelompok] = useState('aksi'); // 'aksi' = aktivitas yang mengubah data, 'akses' = halaman yang dibuka
  const [tingkat, setTingkat] = useState('penting');
  const [dari, setDari] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 6); return tgl(d); });
  const [sampai, setSampai] = useState(() => tgl(new Date()));
  const [cabang, setCabang] = useState('');
  const [peran, setPeran] = useState('');
  const [pengguna, setPengguna] = useState('');
  const [kategori, setKategori] = useState('');
  const [q, setQ] = useState('');
  const [halaman, setHalaman] = useState(0);
  const [batas, setBatas] = useState(50);
  const [hapus, setHapus] = useState(false);
  const [aturMasa, setAturMasa] = useState(false);
  const [notif, setNotif] = useState(null);
  const [mengekspor, setMengekspor] = useState(false);

  const query = useMemo(() => new URLSearchParams({
    kelompok, tingkat, dari, sampai, halaman: String(halaman + 1), batas: String(batas),
    ...(cabang && { cabang }), ...(peran && { peran }), ...(pengguna && { pengguna }), ...(kategori && { kategori }), ...(q.trim() && { q: q.trim() }),
  }).toString(), [kelompok, tingkat, dari, sampai, halaman, batas, cabang, peran, pengguna, kategori, q]);

  const { data, error, isLoading, mutate } = useSWR(`${base()}/log-aktivitas?${query}`, ambil, { revalidateOnFocus: false });
  const { data: opsi } = useSWR(`${base()}/log-aktivitas/opsi${cabang ? `?cabang=${cabang}` : ''}`, ambil, { revalidateOnFocus: false });
  const list = data?.data || [];
  const retensi = infoRetensi(data?.retensi);
  const ringkasan = data?.ringkasan || { penting: 0, biasa: 0 };

  const ubahFilter = (fn) => (e) => { fn(e.target.value); setHalaman(0); };

  const ekspor = async () => {
    setMengekspor(true);
    try {
      const sama = new URLSearchParams(query); sama.set('halaman', '1'); sama.set('batas', '5000'); sama.set('ekspor', '1');
      const r = await ambil(`${base()}/log-aktivitas?${sama.toString()}`);
      const csv = buatCsv(r.data || [], { lihatIp: !!r.lihatIp, tampilkanCabang: superadmin });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = `log-aktivitas-${tgl(new Date())}.csv`; a.click();
      URL.revokeObjectURL(url);
      axios.post(`${base()}/log-aktivitas/ekspor`, { nama: 'Log aktivitas', format: 'csv', jumlah: (r.data || []).length }, { withCredentials: true }).catch(() => {});
      setNotif(`${(r.data || []).length} baris diekspor${r.total > 5000 ? ' (dibatasi 5.000 baris; persempit tanggal untuk sisanya)' : ''}`);
    } catch (e) {
      setNotif(pesanGalat(e));
    } finally {
      setMengekspor(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>Log Aktivitas</Typography>
        <Button startIcon={<DownloadOutlined />} variant="outlined" onClick={ekspor} disabled={mengekspor || !list.length}>{mengekspor ? 'Mengekspor…' : 'Ekspor CSV'}</Button>
        {data?.bolehHapus && <Button startIcon={<DeleteOutline />} color="error" variant="outlined" onClick={() => setHapus(true)}>Hapus log</Button>}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Apa yang dilakukan kasir, karyawan, dapur, dan admin {superadmin ? 'di semua cabang' : 'di cabang ini'}.
        {!superadmin && ' Aksi superadmin tidak ditampilkan.'}
      </Typography>

      <Alert severity={retensi.tingkat} sx={{ mb: 1.5 }} action={data?.retensi?.bolehAtur ? <Button color="inherit" size="small" onClick={() => setAturMasa(true)}>Atur masa simpan</Button> : undefined}>{retensi.teks}{retensi.tingkat === 'warning' ? ' Gunakan Ekspor CSV bila perlu mengarsipkannya.' : ''}</Alert>

      <ToggleButtonGroup exclusive size="small" value={kelompok} onChange={(_, v) => { if (v) { setKelompok(v); setKategori(''); setHalaman(0); } }} sx={{ mb: 1 }} aria-label="Kelompok log">
        <ToggleButton value="aksi">Aktivitas ({data?.kelompok?.aksi ?? 0})</ToggleButton>
        <ToggleButton value="akses">Akses halaman ({data?.kelompok?.akses ?? 0})</ToggleButton>
      </ToggleButtonGroup>

      <Tabs value={tingkat} onChange={(_, v) => { setTingkat(v); setHalaman(0); }} sx={{ mb: 1 }}>
        <Tab value="penting" label={`Penting (${ringkasan.penting})`} />
        <Tab value="semua" label={`Semua (${ringkasan.penting + ringkasan.biasa})`} />
      </Tabs>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" type="date" label="Dari" value={dari} onChange={ubahFilter(setDari)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Sampai" value={sampai} onChange={ubahFilter(setSampai)} InputLabelProps={{ shrink: true }} />
        {superadmin && (
          <TextField size="small" select label="Cabang" value={cabang} onChange={ubahFilter(setCabang)} sx={{ minWidth: 170 }}>
            <MenuItem value="">Semua cabang</MenuItem>
            {(opsi?.cabang || []).map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </TextField>
        )}
        <TextField size="small" select label="Peran" value={peran} onChange={ubahFilter(setPeran)} sx={{ minWidth: 140 }}>
          <MenuItem value="">Semua peran</MenuItem>
          {(opsi?.peran || []).map((p) => <MenuItem key={p} value={p}>{labelPeran(p)}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Pengguna" value={pengguna} onChange={ubahFilter(setPengguna)} sx={{ minWidth: 170 }}>
          <MenuItem value="">Semua pengguna</MenuItem>
          {(opsi?.pengguna || []).map((p) => <MenuItem key={p.uuid} value={p.uuid}>{p.nama || p.username}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Jenis" value={kategori} onChange={ubahFilter(setKategori)} sx={{ minWidth: 150 }}>
          <MenuItem value="">Semua jenis</MenuItem>
          {(opsi?.kategori || []).filter((k) => k !== 'akses').map((k) => <MenuItem key={k} value={k}>{labelKategori(k)}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Cari" value={q} onChange={ubahFilter(setQ)} placeholder="nama, transaksi, kalimat…" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{pesanGalat(error)}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Waktu</TableCell>
              <TableCell>Pengguna</TableCell>
              {superadmin && <TableCell>Cabang</TableCell>}
              <TableCell>Aktivitas</TableCell>
              <TableCell>Lokasi{superadmin ? ' / IP' : ''}</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((r) => <BarisLog key={r.uuid} r={r} superadmin={superadmin} />)}
            {!isLoading && list.length === 0 && (
              <TableRow><TableCell colSpan={superadmin ? 6 : 5} align="center">
                {tingkat === 'penting' && ringkasan.biasa > 0 ? `Tidak ada ${kelompok === 'akses' ? 'akses halaman sensitif' : 'aktivitas penting'} pada pilihan ini. Buka tab Semua untuk yang lain.` : `Belum ada ${kelompok === 'akses' ? 'akses halaman' : 'aktivitas'} pada pilihan ini.`}
              </TableCell></TableRow>
            )}
            {isLoading && <TableRow><TableCell colSpan={superadmin ? 6 : 5} align="center">Memuat...</TableCell></TableRow>}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data?.total || 0} page={halaman} onPageChange={(_, p) => setHalaman(p)}
          rowsPerPage={batas} onRowsPerPageChange={(e) => { setBatas(parseInt(e.target.value, 10)); setHalaman(0); }} rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Baris per halaman" />
      </TableContainer>

      <DialogRetensi open={aturMasa} onClose={() => setAturMasa(false)} retensi={data?.retensi} onSelesai={(p) => { setNotif(p); mutate(); }} />
      <DialogHapus open={hapus} onClose={() => setHapus(false)} cabang={opsi?.cabang} onSelesai={(p) => { setNotif(p); mutate(); }} />
      <Snackbar open={!!notif} autoHideDuration={5000} onClose={() => setNotif(null)} message={notif} />
    </Box>
  );
};

export default LogAktivitas;
